import { mergeWith } from 'lodash-es'
import type { AllPossibleSettingsKeys } from '../../types'
import { Settings } from '../../types/state'
import { SettingsType } from '../constants/enums'
import {
    SETTINGS_HOME_DISPLAY,
    SETTINGS_HOME_REGION,
    SETTINGS_HOME_SHOW,
    SETTINGS_HOME_SORT,
} from '../constants/settings'
import {
    UEOPT_HOME_DISPLAY_REGION,
    UEOPT_HOME_DISPLAY_TYPE,
    UEOPT_HOME_SHOW_BY_TYPE,
    UEOPT_HOME_SORT_BY,
    lookupUeoptById,
} from '../mfcLogic/ueopts'
import { SETTINGSSTATE } from '../state'
import { pubSubSettings } from '../state/pubSub'
import { setCookie } from '../utilities/cookies'
import { updateSettingsAction } from './workerActions'

export const saveSettings = <
    T extends keyof Settings,
    U extends keyof Settings[T],
>(
    type: T,
    key: U,
    value: Settings[T][U],
) => {
    const setting = { type, key, value }
    const mergedValue = updateSettingsAndEmit(setting)
    const mergedSetting = {
        ...setting,
        value: mergedValue,
    }
    updateSettingsAction([mergedSetting])
}

/** Merge `setting` into `SETTINGSSTATE` */
function updateSettings<
    T extends keyof Settings,
    U extends keyof Settings[T],
>(setting: { type: T; key: U; value: Settings[T][U] }) {
    const { type, key, value } = setting
    const updatedSetting: Settings = { [key]: value }
    // create a fresh reference for that type
    const settings = { ...SETTINGSSTATE[type] }
    mergeWith(settings, updatedSetting, (target, update) => {
        if (Array.isArray(target) && Array.isArray(update)) {
            return update
        }
    })
    SETTINGSSTATE[type] = settings
    return SETTINGSSTATE[type][key]
}

export function updateSettingsAndEmit<
    T extends keyof Settings,
    U extends keyof Settings[T],
>(setting: { type: T; key: U; value: Settings[T][U] }) {
    const { key, type } = setting
    const value = updateSettings(setting)
    // any display write (user click, stored UEOPT replay, guest cookie/query
    // restore) counts as an explicit choice; pages that substitute their own
    // default while unchosen (Explore) key off this flag
    if (
        (type as SettingsType) === SettingsType.DEFAULT &&
        (key as string) === 'display'
    ) {
        updateSettings({
            type: SettingsType.DEFAULT,
            key: 'displayChosen',
            value: true,
        })
    }
    const newSettings = SETTINGSSTATE[type]
    if (newSettings) {
        pubSubSettings.emit(type, newSettings, true)
    }
    const ueoptId = lookupUeoptById(key as AllPossibleSettingsKeys)
    setUeoptCookie(ueoptId, value as string | number)
    return value
}

function setUeoptCookie(ueoptId: number, value: string | number) {
    // todo: move to cookie function for settings
    const cookieValue = String(value)
    switch (ueoptId) {
        case UEOPT_HOME_DISPLAY_TYPE:
            setCookie(SETTINGS_HOME_DISPLAY, cookieValue)
            return
        case UEOPT_HOME_DISPLAY_REGION:
            setCookie(SETTINGS_HOME_REGION, cookieValue)
            return
        case UEOPT_HOME_SHOW_BY_TYPE:
            setCookie(SETTINGS_HOME_SHOW, cookieValue)
            return
        case UEOPT_HOME_SORT_BY:
            setCookie(SETTINGS_HOME_SORT, cookieValue)
            return
        default:
            return
    }
}
